/**
 * WorkflowInputPanel - Shows available outputs from previous steps
 *
 * Modern, clean interface for browsing and dragging outputs from
 * triggers and previous actions into parameter fields.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Text,
  Input,
  Tooltip,
  mergeClasses,
  Badge,
} from '@coreai/fluentui-react';
import { thinScrollbarStyles } from '@/styles/scrollbar';
import {
  ChevronRightRegular,
  ChevronDownRegular,
  CopyRegular,
  SearchRegular,
  FlashRegular,
  PlugConnectedRegular,
  PlayRegular,
  BotRegular,
  BracesVariableRegular,
  TagRegular,
} from '@fluentui/react-icons';
import { getTypeIcon, isExpandableType } from '../TokenPicker/typeIcons';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useParameterValues } from '@/hooks/useParameterValues';
import type { Token } from '@/types/valueSegment';
import type { TokenSource } from '@/types/tokenSource';
import type { Action } from '@/types/workflowDefinition';
import {
  flattenAllActions,
  getScopeAwarePredecessors,
} from '@/utils/workflow';
import { ParameterValueSourceTabs, type ValueSource } from '../shared/ParameterValueSourceTabs';
import { RailHintFooter } from '../shared/RailHintFooter';
import { useHoverIntentTooltip } from '../shared/useHoverIntentTooltip';
import { createDragImage, resolveTokenColor } from '../shared/dragImage';
import { FunctionPickerPanel } from './FunctionPickerPanel';
import {
  parseExpression,
  resolveExpressionValue,
  formatResolvedValue,
} from '@/utils/expressions';

/** Filter tokens by search query matching title, name, or description */
function filterTokensBySearch(tokenList: Token[], query: string): Token[] {
  const lowerQuery = query.toLowerCase();
  return tokenList.filter(t =>
    t.title.toLowerCase().includes(lowerQuery) ||
    (t.name?.toLowerCase().includes(lowerQuery)) ||
    (t.description?.toLowerCase().includes(lowerQuery))
  );
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  tabsBar: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  expressionPane: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  searchContainer: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  searchInput: {
    width: '100%',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingVerticalS,
    ...thinScrollbarStyles,
  },

  // Section (trigger or action group)
  section: {
    marginBottom: tokens.spacingVerticalS,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    userSelect: 'none',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  sectionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    overflow: 'hidden',
    // In Windows high-contrast / forced-colors mode, the four colored variants
    // collapse to system Canvas/CanvasText, erasing the chip-shaped affordance.
    // Reinstate it with a thin system-color border so the icon still reads as a
    // grouped category indicator instead of floating on top of the row.
    '@media (forced-colors: active)': {
      border: '1px solid CanvasText',
    },
  },
  sectionTriggerIcon: {
    backgroundColor: tokens.colorPaletteLightGreenBackground2,
    color: tokens.colorPaletteLightGreenForeground2,
  },
  sectionActionIcon: {
    backgroundColor: tokens.colorPaletteBlueBorderActive,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  sectionAgentIcon: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
    color: tokens.colorPaletteBerryForeground2,
  },
  sectionVariableIcon: {
    backgroundColor: tokens.colorPaletteTealBackground2,
    color: tokens.colorPaletteTealForeground2,
  },
  sectionParameterIcon: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  sectionIconImg: {
    width: '20px',
    height: '20px',
    display: 'block',
    objectFit: 'contain' as const,
  },
  sectionInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase300,
  },
  sectionChevron: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  sectionChevronExpanded: {
    transform: 'rotate(90deg)',
  },
  sectionBadge: {
    flexShrink: 0,
  },

  // Outputs list
  outputsList: {
    paddingLeft: '36px', // Align with section content
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },

  outputItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minHeight: '36px',
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    cursor: 'grab',
    borderRadius: tokens.borderRadiusSmall,
    transitionProperty: 'background-color, transform, box-shadow',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      boxShadow: `inset 0 0 0 1px ${tokens.colorNeutralStroke2}`,
    },
    // Light up the always-visible copy button on hover/focus so its role is
    // unmistakable. The copy button now lives outside the row (see
    // outputRowWrapper) so this relationship moves to the wrapper.
    ':active': {
      cursor: 'grabbing',
      transform: 'scale(0.98)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
    '@media (forced-colors: active)': {
      ':hover': {
        outline: '1px solid Highlight',
        outlineOffset: '-1px',
      },
    },
  },
  // Wraps the row and its dedicated copy button. The copy button is a sibling
  // of the row (not a descendant), so the row can stay a role="button" without
  // nesting an interactive control inside it (nested-interactive). Hover or
  // focus anywhere in the wrapper brightens the copy button.
  outputRowWrapper: {
    position: 'relative',
    ':hover [data-copy-button]': {
      opacity: 1,
      color: tokens.colorBrandForeground1,
    },
    ':focus-within [data-copy-button]': {
      opacity: 1,
      color: tokens.colorBrandForeground1,
    },
  },
  // Reserve room on the right for the absolutely-positioned copy button so the
  // row's text never runs underneath it.
  outputItemWithCopy: {
    paddingRight: '40px',
  },
  // Row cursor + press-down feedback for any row that responds to a
  // click — expandable rows (default mode) and any row in insert mode.
  outputItemActionable: {
    cursor: 'pointer',
    ':active': {
      cursor: 'pointer',
      transform: 'scale(0.98)',
    },
  },
  // Always-visible "Copy" button on the right edge of every row. Lives at low
  // opacity so it doesn't compete with the row contents, then pops to brand
  // color on row hover/focus (driven by the parent's `[data-copy-button]`
  // selector). Replaces the previous `ArrowEnter` decoration so the action
  // tied to the icon (copy) matches its appearance, and keeps the row body
  // free for expand-on-click behaviour. Hidden when the "Copied"/"Copy
  // failed" badge takes the same slot, so we never stack the two.
  outputCopyButton: {
    position: 'absolute',
    right: tokens.spacingHorizontalS,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    flexShrink: 0,
    padding: 0,
    color: tokens.colorNeutralForeground3,
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase300,
    opacity: 0.4,
    transitionProperty: 'opacity, color, background-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      opacity: 1,
      color: tokens.colorBrandForeground1,
    },
    ':active': {
      transform: 'translateY(-50%) scale(0.94)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': {
        transform: 'none',
      },
    },
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      ':hover': {
        outline: '1px solid Highlight',
        outlineOffset: '-1px',
      },
    },
  },
  copiedBadge: {
    marginLeft: 'auto',
    flexShrink: 0,
    padding: `2px ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.2px',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.92)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMid,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      backgroundColor: 'Highlight',
      color: 'HighlightText',
    },
  },
  copyFailedBadge: {
    backgroundColor: tokens.colorStatusDangerBackground2,
    color: tokens.colorStatusDangerForeground1,
    '@media (forced-colors: active)': {
      forcedColorAdjust: 'none',
      backgroundColor: 'Canvas',
      color: 'LinkText',
      border: '1px solid LinkText',
    },
  },
  outputItemDragging: {
    opacity: 0.5,
    backgroundColor: tokens.colorNeutralBackground3Pressed,
  },
  outputIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
  },
  outputTypeIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  outputExpandChevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    // Reset native <button> defaults so the same class can be reused for
    // both the decorative div (default mode) and the real button
    // (insert mode) without visually drifting.
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    padding: 0,
    borderRadius: tokens.borderRadiusSmall,
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground4,
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  outputExpandPlaceholder: {
    width: '20px',
    flexShrink: 0,
  },
  /**
   * Per-row pulsing dot rendered in the chevron slot when we don't yet know
   * whether a type-expandable token (object/array) has any concrete children
   * to reveal — i.e., run-data fetch is in flight, schema didn't supply
   * static children, and the row's type can in principle expand. When data
   * lands the dot resolves into either a real chevron (children appeared)
   * or empty space (no children) — no false-positive affordance, just a
   * truthful "checking" signal.
   */
  chevronLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
  chevronLoadingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralForeground3,
    animationName: {
      '0%, 100%': { opacity: 0.3, transform: 'scale(0.7)' },
      '50%':       { opacity: 0.85, transform: 'scale(1)' },
    },
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      opacity: 0.5,
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
    },
  },
  nestedOutputsList: {
    marginLeft: tokens.spacingHorizontalL,
    paddingLeft: tokens.spacingHorizontalS,
    borderLeft: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  outputInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  outputName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase200,
  },
  outputExpression: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase100,
  },
  outputPreview: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorBrandForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: tokens.lineHeightBase100,
    marginTop: tokens.spacingVerticalXXS,
    fontWeight: tokens.fontWeightMedium,
  },
  outputPreviewPlaceholder: {
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero800,
    opacity: 0.5,
  },

  // Hint footer markup is now the shared <RailHintFooter /> primitive in
  // shared/RailHintFooter.tsx — both Dynamic content and Expressions use it.
  noOutputsText: {
    color: tokens.colorNeutralForeground3,
    padding: tokens.spacingVerticalS,
  },
  // Wrapper for the description tooltip content. pointer-events: none so
  // the tooltip surface stays purely informational and can't block clicks
  // on whatever's underneath (mirrors FunctionPickerPanel.tooltipBody).
  tooltipDescription: {
    pointerEvents: 'none',
  },
});

export interface WorkflowInputPanelProps {
  /** Token sources from useTokenSources - contains properly computed outputs per action */
  sources: TokenSource[];
  /** Called when a token is clicked */
  onTokenClick?: ((token: Token) => void) | undefined;
  /** Whether to show search */
  showSearch?: boolean | undefined;
  /** Current node ID to exclude from inputs */
  currentNodeId?: string | undefined;
  /** Action outputs from the last successful run, keyed by action name */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews */
  hasRunData?: boolean | undefined;
  /**
   * Whether the run-data fetch is in flight. Drives an indeterminate progress
   * bar at the top of the panel so users have an ambient signal that chevrons
   * (and resolved-value previews) may still be populating, without
   * false-positive affordances on individual rows.
   */
  isLoadingRunData?: boolean | undefined;
  /**
   * Selected tab in the rail. When `'expr'`, the panel renders
   * {@link FunctionPickerPanel} in place of the token tree.
   * Defaults to `'tokens'` for backward compatibility.
   */
  valueSource?: ValueSource | undefined;
  /**
   * Fired when the user activates a tab. Container (ExpandedView) writes
   * this back to `nodeEditorStore.setInputPanelValueSource` so the chosen
   * tab survives panel-internal re-renders.
   */
  onValueSourceChange?: ((source: ValueSource) => void) | undefined;
  /**
   * Fired when the function-picker panel commits an expression template.
   * Routed by ExpandedView to the focused parameter field.
   */
  onExpressionInsert?: ((template: string) => void) | undefined;
  /**
   * Suppress the panel's own value-source tab strip. Used when the panel
   * is embedded inside a host that already renders its own tabs (e.g.
   * the FieldFork popover). The body still respects `valueSource` so
   * the host can drive between tokens / expr.
   */
  hideTabs?: boolean | undefined;
  /**
   * Insert mode. When `true`, clicking a token row body fires
   * {@link onTokenClick} (treated as the commit action), the chevron is
   * promoted to a real button that owns expand/collapse, and the
   * always-visible copy button is hidden.
   *
   * Used by the FieldFork popover where the user opened the panel
   * specifically to insert a value — the standalone left rail keeps the
   * default model (drag to insert, copy button to copy) because there is
   * no implicit target field there to receive a row-click.
   */
  insertOnRowClick?: boolean | undefined;
  /**
   * What clicking a row in {@link insertOnRowClick} mode does.
   * - `'insert'` (default): fire {@link onTokenClick} so the host commits
   *   the token into the parameter value.
   * - `'copy'`: route the row click through the same clipboard-copy flow
   *   the per-row copy button uses in default mode (writes to clipboard,
   *   flashes the inline "Copied" badge, fires `onTokenClick` for
   *   analytics). Used by composite editors (Authentication, Condition,
   *   etc.) where appending a token segment to the parameter value
   *   would corrupt its structure — the popover surfaces the value so
   *   the user can paste it into the inner sub-field they need.
   *
   * Has no effect when {@link insertOnRowClick} is false.
   */
  rowClickAction?: 'insert' | 'copy' | undefined;
}

/** Get a friendly display name from action/trigger name */
function formatDisplayName(name: string): string {
  // Replace underscores with spaces
  let result = name.replace(/_/g, ' ');

  // Insert space before capitals that follow lowercase letters (camelCase)
  // e.g., "getTeam" -> "get Team"
  result = result.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Insert space before the last capital in a sequence of capitals followed by lowercase
  // e.g., "HTTPRequest" -> "HTTP Request"
  result = result.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

  // Capitalize first letter of each word, preserve rest of casing
  return result
    .trim()
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * WorkflowInputPanel - Modern interface for browsing outputs from previous steps
 */
export const WorkflowInputPanel = ({
  sources,
  onTokenClick,
  showSearch = true,
  currentNodeId,
  actionOutputs,
  hasRunData = false,
  isLoadingRunData = false,
  valueSource = 'tokens',
  onValueSourceChange,
  onExpressionInsert,
  hideTabs = false,
  insertOnRowClick = false,
  rowClickAction = 'insert',
}: WorkflowInputPanelProps) => {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [draggingToken, setDraggingToken] = useState<string | null>(null);
  const [expandedTokens, setExpandedTokens] = useState<Set<string>>(new Set());
  const [failedIcons, setFailedIcons] = useState<Set<string>>(new Set());
  /** Key of a token that was just clicked, used to flash a "Copied" tooltip. */
  const [copiedTokenKey, setCopiedTokenKey] = useState<string | null>(null);
  /** Mirrors copiedTokenKey but for the failure path (clipboard API missing /
   * insecure context / permission denied). Surfaced as a distinct red badge so
   * users don't get false confidence that the value is on their clipboard. */
  const [copyFailedKey, setCopyFailedKey] = useState<string | null>(null);
  const copyTimerRef = useRef<number | null>(null);
  // Controlled tooltip with hover-intent delay. Shared with the Expressions
  // tab (FunctionPickerPanel) — see {@link useHoverIntentTooltip} for the
  // rationale (Fluent's hover machinery doesn't fire mouseleave during
  // HTML5 drag, so we control visibility explicitly and force-close on
  // drag start).
  const {
    openKey: openTooltipKey,
    scheduleShow: scheduleTooltipShow,
    close: closeTooltip,
    setOpenKey: setOpenTooltipKey,
  } = useHoverIntentTooltip(600);

  // Reset the copy-feedback timer on unmount so we never call setState after unmount.
  useEffect(() => () => {
    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
  }, []);

  // Get workflow definition
  const triggers = useWorkflowStore((s) => s.workflow?.definition?.triggers ?? {});
  const actions = useWorkflowStore((s) => s.workflow?.definition?.actions ?? {});

  // Resolved `@parameters('name')` values for inline previews on parameter
  // tokens — known statically, so no run data required.
  const parameterValues = useParameterValues();

  // Extract the action name from the node ID. Nested actions use a path
  // form like "action-Foreach/HTTP" — strip prefix then take the leaf
  // segment (action names are workflow-globally unique).
  const currentActionName = useMemo(() => {
    if (!currentNodeId) return null;
    let raw = currentNodeId;
    if (raw.startsWith('action-')) {
      raw = raw.slice('action-'.length);
    } else if (raw.startsWith('trigger-')) {
      raw = raw.slice('trigger-'.length);
    }
    const lastSlash = raw.lastIndexOf('/');
    return lastSlash >= 0 ? raw.slice(lastSlash + 1) : raw;
  }, [currentNodeId]);

  // Get predecessor actions (actions that run before current node).
  // Uses the shared scope-aware helper so siblings inside the current
  // action's scope (Foreach/If/Switch/etc.) are surfaced too — without
  // this, an action sitting in the middle of a ForEach loop would only
  // see what ran before the loop, not the earlier loop body actions.
  const predecessorActions = useMemo(() => {
    return getScopeAwarePredecessors(
      currentActionName ?? undefined,
      actions as Record<string, Action>,
    );
  }, [actions, currentActionName]);

  // Walk the FLATTENED action tree so nested predecessors render as
  // selectable token sections (otherwise iterating top-level `actions`
  // alone hides everything inside scopes).
  const flatActions = useMemo(
    () => flattenAllActions(actions as Record<string, Action>),
    [actions],
  );

  // Get tokens for a specific source
  const getTokensForSource = useCallback((sourceName: string, isTrigger: boolean): Token[] => {
    const sourceId = isTrigger ? 'triggers' : `action_${sourceName}`;
    const source = sources.find((s) => s.id === sourceId);
    if (!source) return [];

    if (isTrigger) {
      return source.tokens.filter((t) => t.actionName === sourceName);
    }

    return source.tokens;
  }, [sources]);

  // Get agent parameters from sources
  const agentParameterSource = useMemo(() => {
    return sources.find(s => s.id === 'agentParameters');
  }, [sources]);

  // Check if trigger tokens are available in sources
  // (they won't be if the node is detached from the trigger)
  const triggerSource = useMemo(() => {
    return sources.find(s => s.id === 'triggers');
  }, [sources]);

  // Get variables from sources
  const variablesSource = useMemo(() => {
    return sources.find(s => s.id === 'variables');
  }, [sources]);

  // Get workflow parameters from sources
  const parametersSource = useMemo(() => {
    return sources.find(s => s.id === 'parameters');
  }, [sources]);

  // Get ForEach loop tokens from sources
  const foreachSource = useMemo(() => {
    return sources.find(s => s.id === 'foreachItems');
  }, [sources]);

  // Build the list of sections (agent parameters + workflow parameters + variables + triggers + actions with their tokens)
  const sections = useMemo(() => {
    const result: Array<{
      id: string;
      name: string;
      displayName: string;
      type: 'trigger' | 'action' | 'agentParameters' | 'variables' | 'parameters';
      tokens: Token[];
      brandColor?: string | undefined;
      iconUri?: string | undefined;
    }> = [];

    // Add agent parameters section at the top (if available)
    if (agentParameterSource && agentParameterSource.tokens.length > 0) {
      const agentTokens = searchQuery
        ? filterTokensBySearch(agentParameterSource.tokens, searchQuery)
        : agentParameterSource.tokens;
      if (agentTokens.length > 0) {
        result.push({
          id: 'agentParameters',
          name: 'Agent Parameters',
          displayName: 'Agent Parameters',
          type: 'agentParameters',
          tokens: agentTokens,
        });
      }
    }

    // Add workflow parameters section (workflow-scope, always available regardless of predecessors)
    if (parametersSource && parametersSource.tokens.length > 0) {
      const paramTokens = searchQuery
        ? filterTokensBySearch(parametersSource.tokens, searchQuery)
        : parametersSource.tokens;
      if (paramTokens.length > 0) {
        result.push({
          id: 'parameters',
          name: 'Parameters',
          displayName: 'Parameters',
          type: 'parameters',
          tokens: paramTokens,
          brandColor: parametersSource.brandColor,
        });
      }
    }

    // Add variables section near the top (after agent parameters, before triggers)
    if (variablesSource && variablesSource.tokens.length > 0) {
      const variableTokens = searchQuery
        ? filterTokensBySearch(variablesSource.tokens, searchQuery)
        : variablesSource.tokens;
      if (variableTokens.length > 0) {
        result.push({
          id: 'variables',
          name: 'Variables',
          displayName: 'Variables',
          type: 'variables',
          tokens: variableTokens,
        });
      }
    }

    // Add triggers - only if trigger tokens are available in sources
    // (triggers won't be available for nodes that are detached from the trigger)
    if (triggerSource) {
      Object.keys(triggers).forEach((name) => {
        const tokens = getTokensForSource(name, true);
        // Filter by search
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesName = name.toLowerCase().includes(query);
          const matchesTokens = tokens.some(t =>
            t.title.toLowerCase().includes(query) ||
            (t.name?.toLowerCase().includes(query)) ||
            (t.value?.toLowerCase().includes(query))
          );
          if (!matchesName && !matchesTokens) return;
        }
        result.push({
          id: `trigger_${name}`,
          name,
          displayName: formatDisplayName(name),
          type: 'trigger',
          tokens,
          brandColor: triggerSource.brandColor,
          iconUri: triggerSource.iconUri,
        });
      });
    }

    // Add predecessor actions (skip InitializeVariable actions - they have no meaningful outputs)
    Object.entries(flatActions).forEach(([name, action]) => {
      if (!predecessorActions.has(name) || name === currentActionName) return;

      // Skip variable-mutation actions — InitializeVariable/SetVariable values
      // are accessed via the variables() function (rendered as the dedicated
      // Variables section above), not as action outputs.
      const actionType = (action as { type?: string })?.type?.toLowerCase() ?? '';
      if (actionType === 'initializevariable' || actionType === 'setvariable') return;

      const tokens = getTokensForSource(name, false);
      // Filter by search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = name.toLowerCase().includes(query);
        const matchesTokens = tokens.some(t =>
          t.title.toLowerCase().includes(query) ||
          (t.name?.toLowerCase().includes(query)) ||
          (t.value?.toLowerCase().includes(query))
        );
        if (!matchesName && !matchesTokens) return;
      }
      const actionSource = sources.find(s => s.id === `action_${name}`);
      result.push({
        id: `action_${name}`,
        name,
        displayName: formatDisplayName(name),
        type: 'action',
        tokens,
        brandColor: actionSource?.brandColor,
        iconUri: actionSource?.iconUri,
      });
    });

    // Add ForEach loop tokens as an action section for the parent loop
    // These appear as outputs of the ForEach action (items, iterationIndexes)
    if (foreachSource && foreachSource.tokens.length > 0) {
      const foreachTokens = searchQuery
        ? filterTokensBySearch(foreachSource.tokens, searchQuery)
        : foreachSource.tokens;
      if (foreachTokens.length > 0) {
        result.push({
          id: `action_foreach_${foreachSource.name}`,
          name: foreachSource.name,
          displayName: foreachSource.name,
          type: 'action',
          tokens: foreachTokens,
          brandColor: foreachSource.brandColor,
          iconUri: foreachSource.iconUri,
        });
      }
    }

    return result;
  }, [agentParameterSource, foreachSource, triggerSource, variablesSource, parametersSource, triggers, flatActions, predecessorActions, currentActionName, getTokensForSource, searchQuery, sources]);

  // Workflow parameters are workflow-scope reference data — keep the section
  // visible but collapsed by default so it doesn't push trigger/action
  // outputs (the more common pick) below the fold.
  const COLLAPSED_BY_DEFAULT_IDS = useRef<ReadonlySet<string>>(new Set(['parameters'])).current;

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(
      sections
        .filter((s) => s.tokens.length > 0 && !COLLAPSED_BY_DEFAULT_IDS.has(s.id))
        .map((s) => s.id),
    ),
  );

  const seenForNodeRef = useRef<{ nodeId: string | null | undefined; seenIds: Set<string> }>({
    nodeId: currentNodeId,
    seenIds: new Set(expandedSections),
  });
  useEffect(() => {
    if (sections.length === 0) return;
    const sectionsWithTokens = sections
      .filter((s) => s.tokens.length > 0)
      .map((s) => s.id);
    const isNewNode = seenForNodeRef.current.nodeId !== currentNodeId;

    if (isNewNode) {
      // Node navigation: reset both the seen set and the visible expansion
      // state to the new node's currently-known sections.
      seenForNodeRef.current = {
        nodeId: currentNodeId,
        seenIds: new Set(sectionsWithTokens),
      };
      setExpandedSections(
        new Set(sectionsWithTokens.filter((id) => !COLLAPSED_BY_DEFAULT_IDS.has(id))),
      );
      return;
    }

    const newIds: string[] = [];
    for (const id of sectionsWithTokens) {
      if (!seenForNodeRef.current.seenIds.has(id)) {
        newIds.push(id);
        seenForNodeRef.current.seenIds.add(id);
      }
    }
    if (newIds.length === 0) return;
    setExpandedSections((prev) => {
      const next = new Set(prev);
      for (const id of newIds) {
        if (COLLAPSED_BY_DEFAULT_IDS.has(id)) continue;
        next.add(id);
      }
      return next;
    });
  }, [sections, currentNodeId, COLLAPSED_BY_DEFAULT_IDS]);

  // Reset to the Tokens tab when the user opens a different node — the
  // function picker is a per-task choice, not a sticky preference.
  const tabResetRef = useRef<{ nodeId: string | null | undefined } | null>(null);
  useEffect(() => {
    if (tabResetRef.current !== null && tabResetRef.current.nodeId === currentNodeId) return;
    const isFirstVisit = tabResetRef.current === null;
    tabResetRef.current = { nodeId: currentNodeId };
    // Only fire the reset on actual node *changes* — not on initial mount —
    // so a parent-controlled `valueSource='expr'` survives first paint.
    if (!isFirstVisit && valueSource !== 'tokens') {
      onValueSourceChange?.('tokens');
    }
  }, [currentNodeId, valueSource, onValueSourceChange]);

  // Toggle section expansion
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Toggle token expansion (for nested tokens)
  const toggleTokenExpansion = useCallback((tokenKey: string, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setExpandedTokens(prev => {
      const next = new Set(prev);
      if (next.has(tokenKey)) {
        next.delete(tokenKey);
      } else {
        next.add(tokenKey);
      }
      return next;
    });
  }, []);

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, token: Token) => {
    setDraggingToken(token.key);
    e.dataTransfer.setData('text/plain', token.value ?? '');
    e.dataTransfer.setData('application/json', JSON.stringify(token));
    e.dataTransfer.effectAllowed = 'copy';

    // Build the custom drag chip via the shared helper. The chip lives in
    // <body> (outside the FluentProvider scope), so we pass *resolved*
    // values: the token's brand color when the connector defines one, or
    // the Fluent brand background resolved against the source row (which
    // IS inside the provider) as a fallback. The trailing `||` literals
    // are a final safety net for environments where the var doesn't
    // resolve (jsdom tests, theme not loaded yet).
    const sourceEl = e.currentTarget as HTMLElement;
    const computed = window.getComputedStyle(sourceEl);
    const resolvedBrand = resolveTokenColor(sourceEl, tokens.colorBrandBackground);
    const fallbackBrand = resolvedBrand || '#0078d4';
    const resolvedOnBrand = resolveTokenColor(sourceEl, tokens.colorNeutralForegroundOnBrand);
    const color = resolvedOnBrand || '#ffffff';
    createDragImage(e.dataTransfer, {
      text: token.title,
      backgroundColor: token.brandColor ?? fallbackBrand,
      color,
      fontFamily: computed.fontFamily,
    });
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggingToken(null);
  }, []);

  // Handle the dedicated copy button click. Owns the clipboard write with
  // explicit success/failure feedback. `onTokenClick` still fires first so
  // callers can hook analytics.
  const handleCopyClick = useCallback(async (token: Token) => {
    onTokenClick?.(token);
    if (!token.value) return;

    if (copyTimerRef.current !== null) {
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }

    let success = false;
    try {
      if (typeof navigator.clipboard?.writeText !== 'function') {
        throw new Error('clipboard API unavailable');
      }
      await navigator.clipboard.writeText(token.value);
      success = true;
    } catch {
      success = false;
    }

    if (success) {
      setCopyFailedKey(null);
      setCopiedTokenKey(token.key);
      copyTimerRef.current = window.setTimeout(() => {
        setCopiedTokenKey(null);
        copyTimerRef.current = null;
      }, 1500);
    } else {
      setCopiedTokenKey(null);
      setCopyFailedKey(token.key);
      copyTimerRef.current = window.setTimeout(() => {
        setCopyFailedKey(null);
        copyTimerRef.current = null;
      }, 2000);
    }
  }, [onTokenClick]);

  // Resolve preview value for a token - returns raw value for object expansion
  const getTokenPreviewRaw = useCallback((token: Token): { value: unknown; resolved: boolean } | null => {
    // Workflow-parameter tokens resolve from app-level + workflow-def values,
    // not from run data — we already know the configured value statically.
    if (token.tokenType === 'parameter' && token.name && token.name in parameterValues) {
      return { value: parameterValues[token.name], resolved: true };
    }

    if (!hasRunData || !actionOutputs || !token.value) {
      return null;
    }

    // Parse the token expression
    const parsed = parseExpression(token.value);
    if (!parsed) {
      return null;
    }

    // Handle variable expressions
    if (parsed.isVariable && parsed.variableName) {
      const variables = actionOutputs['__variables__'] as Record<string, unknown> | undefined;
      if (variables && parsed.variableName in variables) {
        let value = variables[parsed.variableName];
        // Navigate any additional path
        for (const key of parsed.path) {
          if (value === null || value === undefined || typeof value !== 'object') {
            return null;
          }
          value = (value as Record<string, unknown>)[key];
        }
        return { value, resolved: true };
      }
      return null;
    }

    // Get the output for this action
    const actionKey = parsed.isTrigger ? 'trigger' : parsed.actionName;
    const output = actionOutputs[actionKey];

    if (output === undefined) {
      return null;
    }

    // Try to resolve the value
    const resolved = resolveExpressionValue(parsed, output);
    if (resolved.resolved) {
      return { value: resolved.value, resolved: true };
    }

    return null;
  }, [hasRunData, actionOutputs, parameterValues]);

  // Format preview for display
  const getTokenPreview = useCallback((token: Token): { value: string; resolved: boolean } | null => {
    const raw = getTokenPreviewRaw(token);
    if (!raw) return null;
    return { value: formatResolvedValue(raw.value), resolved: true };
  }, [getTokenPreviewRaw]);

  // Generate dynamic child tokens from a resolved object value
  // depth parameter tracks nesting level to prevent infinite expansion
  const generateDynamicChildren = useCallback((
    parentToken: Token,
    resolvedValue: unknown,
    parentExpression: string,
    depth: number = 0
  ): Token[] => {
    // Limit depth to prevent infinite expansion
    const MAX_DEPTH = 5;
    if (depth >= MAX_DEPTH || resolvedValue === null || resolvedValue === undefined) {
      return [];
    }

    const children: Token[] = [];
    const baseExpression = parentExpression.slice(2, -1); // Remove ${ and }

    if (Array.isArray(resolvedValue)) {
      // For arrays, show indexed items (limit to first 10 for performance)
      const maxItems = Math.min(resolvedValue.length, 10);
      const parentName = parentToken.name ?? parentToken.title;
      for (let i = 0; i < maxItems; i++) {
        const itemValue = resolvedValue[i];
        const itemType = typeof itemValue === 'object' && itemValue !== null
          ? (Array.isArray(itemValue) ? 'array' : 'object')
          : typeof itemValue;

        const childExpression = `\${${baseExpression}[${i}]}`;
        const itemTitle = `${parentName}[${i}]`;
        const childToken: Token = {
          key: `${parentToken.key}_${i}`,
          title: itemTitle,
          name: itemTitle,
          type: itemType,
          tokenType: parentToken.tokenType,
          value: childExpression,
          description: itemType === 'object'
            ? 'Expand to see properties'
            : itemType === 'array'
            ? 'Expand to see items'
            : `${itemType} value`,
        };
        if (parentToken.actionName) childToken.actionName = parentToken.actionName;
        if (parentToken.brandColor) childToken.brandColor = parentToken.brandColor;

        // Pre-generate children for nested objects/arrays (to show expand chevron)
        if (itemType === 'object' || itemType === 'array') {
          const nestedChildren = generateDynamicChildren(childToken, itemValue, childExpression, depth + 1);
          if (nestedChildren.length > 0) {
            childToken.children = nestedChildren;
          }
        }
        children.push(childToken);
      }
      if (resolvedValue.length > maxItems) {
        const moreToken: Token = {
          key: `${parentToken.key}_more`,
          title: `${parentName}: ... ${resolvedValue.length - maxItems} more items`,
          name: `... ${resolvedValue.length - maxItems} more`,
          type: 'string',
          tokenType: parentToken.tokenType,
          value: parentExpression,
          description: `Array has ${resolvedValue.length} total items`,
        };
        if (parentToken.brandColor) moreToken.brandColor = parentToken.brandColor;
        children.push(moreToken);
      }
    } else if (typeof resolvedValue === 'object') {
      // For objects, show each property
      const entries = Object.entries(resolvedValue as Record<string, unknown>);
      for (const [key, value] of entries) {
        const valueType = typeof value === 'object' && value !== null
          ? (Array.isArray(value) ? 'array' : 'object')
          : typeof value;

        // Use bracket notation for keys with special characters, dot notation otherwise
        const needsBrackets = !/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(key);
        const childExpression = needsBrackets
          ? `\${${baseExpression}['${key}']}`
          : `\${${baseExpression}.${key}}`;

        const childToken: Token = {
          key: `${parentToken.key}_${key}`,
          title: key,
          name: key,
          type: valueType,
          tokenType: parentToken.tokenType,
          value: childExpression,
          description: valueType === 'object'
            ? 'Expand to see properties'
            : valueType === 'array'
            ? 'Expand to see items'
            : `${valueType} value`,
        };
        if (parentToken.actionName) childToken.actionName = parentToken.actionName;
        if (parentToken.brandColor) childToken.brandColor = parentToken.brandColor;

        // Pre-generate children for nested objects/arrays (to show expand chevron)
        if (valueType === 'object' || valueType === 'array') {
          const nestedChildren = generateDynamicChildren(childToken, value, childExpression, depth + 1);
          if (nestedChildren.length > 0) {
            childToken.children = nestedChildren;
          }
        }
        children.push(childToken);
      }
    }

    return children;
  }, []);

  const isEmpty = sections.length === 0;

  // Recursive token rendering function
  const renderToken = (token: Token, depth: number = 0) => {
    const isDragging = draggingToken === token.key;
    const isCopied = copiedTokenKey === token.key;
    const isCopyFailed = copyFailedKey === token.key;
    const hasStaticChildren = token.children && token.children.length > 0;
    const isTokenExpanded = expandedTokens.has(token.key);
    const preview = getTokenPreview(token);
    const previewRaw = getTokenPreviewRaw(token);

    // Check if the resolved value is actually expandable (object/array, not undefined/null).
    // An empty {} or [] is *not* expandable: the chevron would promise children
    // that `generateDynamicChildren` can't produce, leaving the user clicking a
    // dead disclosure. This shows up most often on Compose outputs whose payload
    // resolves to an empty object/array.
    const resolvedIsExpandable = !!(
      previewRaw?.resolved &&
      previewRaw.value !== undefined &&
      previewRaw.value !== null &&
      typeof previewRaw.value === 'object' &&
      (Array.isArray(previewRaw.value)
        ? (previewRaw.value as unknown[]).length > 0
        : Object.keys(previewRaw.value as Record<string, unknown>).length > 0)
    );

    // Show the chevron only when there's evidence of children — either static
    // schema children or a resolved object/array from run data. We deliberately
    // *don't* fall back to `isExpandableType(token.type)` here: that would light
    // a chevron on every object-typed token (body, outputs, headers, queries,
    // relativePathParameters …) even when most of them have no concrete
    // properties to reveal, which produces a misleading affordance. If chevrons
    // arrive late for some tokens, the right fix is upstream — preload the
    // schemas / run data — not optimistic UI here.
    const canExpand = hasStaticChildren || resolvedIsExpandable;

    // Generate dynamic children from resolved value if expanded
    // For arrays with run data, prefer dynamic children (indexed items like records[0])
    // over static schema children (property shapes like date, amount) since array items
    // need index-based access
    let childrenToRender: Token[] = [];
    if (isTokenExpanded) {
      const canGenerateDynamic = previewRaw?.resolved && token.value && resolvedIsExpandable;
      const resolvedIsArray = canGenerateDynamic && Array.isArray(previewRaw.value);
      // Prefer dynamic children for arrays (need indexed access) or when no static children exist
      const preferDynamic = resolvedIsArray || !hasStaticChildren;

      if (canGenerateDynamic && preferDynamic) {
        childrenToRender = generateDynamicChildren(token, previewRaw!.value, token.value!);
      } else if (hasStaticChildren) {
        childrenToRender = token.children!;
      }
    }
    const hasChildren = childrenToRender.length > 0;

    // Render a small pulsing dot in the chevron slot only when:
    //   1. we don't currently have evidence of children (canExpand is false),
    //   2. the run-data fetch is still in flight, and
    //   3. this token's type *could* in principle become expandable.
    // String-typed leaves never get the dot — we already know they're terminal.
    // When data lands the dot will be replaced by either a real chevron
    // (children appeared) or empty space (none) without claiming the row is
    // expandable while we're still checking.
    const isCheckingChildren = !canExpand && isLoadingRunData && isExpandableType(token.type);

    // Don't show preview for expandable objects/arrays - user can expand to see structure.
    // Also skip empty values (undefined/null/'') because formatResolvedValue would render
    // them as the literal strings 'undefined' or 'null', which is noise for authoring.
    const previewValueIsMeaningful = previewRaw?.resolved
      && previewRaw.value !== undefined
      && previewRaw.value !== null
      && previewRaw.value !== '';
    const showPreview = preview?.resolved && !resolvedIsExpandable && previewValueIsMeaningful;

    // Build tooltip content. The inline "Copied" badge already covers the
    // copied feedback, so the tooltip only carries the description (when one
    // exists). The inline preview row already shows the resolved value, so we
    // don't duplicate it here either. The wrapper has pointer-events: none
    // so the tooltip surface can't intercept clicks on whatever sits
    // beneath it (e.g., the section header above the row).
    const description = token.description?.trim();
    const tooltipContent: React.ReactNode = description && description.length > 0
      ? <span className={styles.tooltipDescription}>{description}</span>
      : null;

    // Click model:
    //   - Default (left rail): row body expands/collapses, copy is owned
    //     by the dedicated copy button, insertion is drag-only.
    //   - Insert mode (FieldFork popover): row body commits via
    //     `commitRow`. The chevron is promoted to its own button so
    //     users can still expand without committing; the copy button is
    //     hidden because the row click already commits.
    const commitRow = () => {
      closeTooltip();
      if (rowClickAction === 'copy') {
        void handleCopyClick(token);
      } else {
        onTokenClick?.(token);
      }
    };
    const insertVerb = rowClickAction === 'copy' ? 'copy' : 'insert';
    const ariaLabel = isCopied
      ? `${token.title} — copied to clipboard`
      : isCopyFailed
        ? `${token.title} — copy failed`
        : insertOnRowClick
          ? canExpand
            ? `${token.title} — click to ${insertVerb}, or use the chevron to ${isTokenExpanded ? 'collapse' : 'expand'} nested fields`
            : `${token.title} — click to ${insertVerb}`
          : canExpand
            ? `${token.title} — click to ${isTokenExpanded ? 'collapse' : 'expand'} nested fields, drag onto a field to insert, or use the copy button to copy the value`
            : `${token.title} — drag onto a field to insert, or use the copy button to copy the value`;
    const copyButtonLabel = `Copy ${token.title} value to clipboard`;
    const chevronButtonLabel = `${isTokenExpanded ? 'Collapse' : 'Expand'} ${token.title}`;

    // Dedicated copy button. Rendered as a sibling of the row (see the wrapper
    // in the return below) rather than a child, so the row can stay a
    // role="button" without nesting an interactive control (nested-interactive).
    // Shown only in default mode, and only when no copy badge occupies the slot.
    const showCopyButton = !insertOnRowClick && !isCopied && !isCopyFailed;
    const copyButton = showCopyButton ? (
      <button
        type="button"
        data-copy-button
        className={styles.outputCopyButton}
        onClick={(e) => {
          e.stopPropagation();
          void handleCopyClick(token);
        }}
        onKeyDown={(e) => {
          // Stop the row's keyboard handler from also firing when the user
          // activates the copy button.
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
        // Drag should originate on the row, not the button — the button is a
        // small icon target, dragging off it feels accidental.
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        aria-label={copyButtonLabel}
        title={copyButtonLabel}
      >
        <CopyRegular aria-hidden="true" />
      </button>
    ) : null;

    const row = (
      <div
        className={mergeClasses(
          styles.outputItem,
          (insertOnRowClick || canExpand) && styles.outputItemActionable,
          showCopyButton && styles.outputItemWithCopy,
          isDragging && styles.outputItemDragging
        )}
        draggable
        onDragStart={(e) => {
          // Wipe tooltip state up front so HTML5 drag's pointer capture
          // can't keep the tooltip open over the drop target.
          closeTooltip();
          handleDragStart(e, token);
        }}
        onDragEnd={handleDragEnd}
        onClick={insertOnRowClick
          ? commitRow
          : canExpand
            ? (e) => {
                closeTooltip();
                toggleTokenExpansion(token.key, e);
              }
            : undefined}
        onMouseEnter={() => scheduleTooltipShow(token.key)}
        onMouseLeave={closeTooltip}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (insertOnRowClick) {
            // Enter / Space commits; ArrowLeft / ArrowRight still drive
            // expand/collapse so keyboard users can browse without
            // committing.
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              commitRow();
            } else if (canExpand && e.key === 'ArrowRight' && !isTokenExpanded) {
              e.preventDefault();
              toggleTokenExpansion(token.key, e);
            } else if (canExpand && e.key === 'ArrowLeft' && isTokenExpanded) {
              e.preventDefault();
              toggleTokenExpansion(token.key, e);
            }
            return;
          }
          if ((e.key === 'Enter' || e.key === ' ') && canExpand) {
            e.preventDefault();
            toggleTokenExpansion(token.key, e);
          } else if (canExpand && e.key === 'ArrowRight' && !isTokenExpanded) {
            e.preventDefault();
            toggleTokenExpansion(token.key, e);
          } else if (canExpand && e.key === 'ArrowLeft' && isTokenExpanded) {
            e.preventDefault();
            toggleTokenExpansion(token.key, e);
          }
        }}
        aria-label={ariaLabel}
        // Only expose aria-expanded on the row when activating it
        // toggles expansion. In insert mode Enter/Space commits the
        // token and the chevron <button> owns the disclosure semantics,
        // so advertising expanded/collapsed here would mislead AT users
        // into expecting Enter to expand.
        aria-expanded={!insertOnRowClick && canExpand ? isTokenExpanded : undefined}
      >
        {/* Expand/collapse chevron.
            - Default mode: decorative — keyboard users press Enter/Space
              (or ArrowLeft/Right) on the row to toggle, so the chevron
              is hidden from assistive tech and isn't separately focusable.
            - Insert mode: promoted to a real <button> with its own
              aria-label and stopPropagation, so users can expand without
              the row's click handler also firing the insert. */}
        {canExpand ? (
          insertOnRowClick ? (
            <button
              type="button"
              className={styles.outputExpandChevron}
              onClick={(e) => {
                e.stopPropagation();
                toggleTokenExpansion(token.key, e);
              }}
              onKeyDown={(e) => {
                // Stop the row's keyboard handler from also firing when
                // the user activates the chevron button.
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                }
              }}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              aria-label={chevronButtonLabel}
              aria-expanded={isTokenExpanded}
              title={chevronButtonLabel}
            >
              {isTokenExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
            </button>
          ) : (
            <div
              className={styles.outputExpandChevron}
              aria-hidden="true"
            >
              {isTokenExpanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
            </div>
          )
        ) : isCheckingChildren ? (
          <div
            className={styles.chevronLoading}
            aria-hidden="true"
            data-testid="chevron-loading"
          >
            <span className={styles.chevronLoadingDot} />
          </div>
        ) : (
          <div className={styles.outputExpandPlaceholder} aria-hidden="true" />
        )}

        {/* Type icon */}
        <span className={styles.outputTypeIcon}>
          {getTypeIcon(token.type)}
        </span>

        <div className={styles.outputInfo}>
          <Text block className={styles.outputName}>{token.title ?? token.name}</Text>
          <Text block className={styles.outputExpression}>{token.value}</Text>
          {showPreview && (
            <Text block className={styles.outputPreview}>
              → {preview!.value}
            </Text>
          )}
        </div>

        {/* Inline copy-feedback flash. Replaces the old tooltip-hijack pattern so the
            tooltip can stay uncontrolled (Fluent forbids switching modes) and
            so the feedback never floats over the parameter panel on the right.
            Branches on success vs. failure so users don't get false confidence
            after a denied or unavailable clipboard write. The dedicated copy
            button itself lives outside the row (see copyButton / the wrapper in
            the return) so the row can stay a role="button" without nesting an
            interactive control. The badge is non-focusable (role="status"), so it
            stays in-flow here. */}
        {(isCopied || isCopyFailed) && (
          <span
            className={mergeClasses(
              styles.copiedBadge,
              isCopyFailed && styles.copyFailedBadge
            )}
            role="status"
            aria-live="polite"
          >
            {isCopied ? 'Copied' : 'Copy failed'}
          </span>
        )}
      </div>
    );

    return (
      <div key={token.key}>
        {/* Relative wrapper holds the row plus the copy button as siblings, so
            the copy button is not a focusable descendant of the role="button"
            row (nested-interactive). */}
        <div className={styles.outputRowWrapper}>
          {tooltipContent !== null ? (
            <Tooltip
              content={tooltipContent}
              relationship="description"
              positioning="above-start"
              visible={openTooltipKey === token.key && !isDragging}
              onVisibleChange={(_, data) => {
                // Honour close-only events from Fluent (Esc, focus moves)
                // while keeping our hover-intent timer as the single
                // source of truth for *opening* the tooltip.
                if (!data.visible && openTooltipKey === token.key) {
                  setOpenTooltipKey(null);
                }
              }}
            >
              {row}
            </Tooltip>
          ) : (
            row
          )}
          {copyButton}
        </div>

        {/* Render nested children (static or dynamic from run data) */}
        {isTokenExpanded && hasChildren && (
          <div className={styles.nestedOutputsList}>
            {childrenToRender.map((child) => renderToken(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/*
        Sticky tab strip drives a content swap. `'tokens'` shows the tree;
        `'expr'` shows the function picker. Hosts that render their own tab
        strip (e.g. FieldFork popover) pass `hideTabs` — the body still
        respects `valueSource`.
      */}
      {!hideTabs && (
        <div className={styles.tabsBar}>
          <ParameterValueSourceTabs
            sticky
            selected={valueSource}
            onSelect={(source) => onValueSourceChange?.(source)}
          />
        </div>
      )}

      {valueSource === 'expr' ? (
        <div className={styles.expressionPane}>
          <FunctionPickerPanel
            {...(onExpressionInsert ? { onInsert: onExpressionInsert } : {})}
          />
        </div>
      ) : (
        <>
          {showSearch && (
            <div className={styles.searchContainer}>
              <Input
                className={styles.searchInput}
                contentBefore={<SearchRegular />}
                placeholder="Search inputs"
                value={searchQuery}
                onChange={(_, data) => setSearchQuery(data.value)}
                aria-label="Search inputs"
                size="small"
              />
            </div>
          )}

      <div className={styles.content}>
        {isEmpty ? (
          <div className={styles.emptyState}>
            <PlugConnectedRegular className={styles.emptyIcon} />
            <Text size={300} weight="semibold">No outputs available</Text>
            <Text size={200}>
              {searchQuery ? 'No matches found' : 'Add steps before this node to use their outputs'}
            </Text>
          </div>
        ) : (
          sections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const hasTokens = section.tokens.length > 0;
            const fallbackIconClass = !section.iconUri && ({
              trigger: styles.sectionTriggerIcon,
              action: styles.sectionActionIcon,
              agentParameters: styles.sectionAgentIcon,
              variables: styles.sectionVariableIcon,
              parameters: styles.sectionParameterIcon,
            } as Record<string, string>)[section.type];

            return (
              <div key={section.id} className={styles.section}>
                {/* Section Header */}
                <div
                  className={styles.sectionHeader}
                  onClick={() => toggleSection(section.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSection(section.id); }}
                  aria-expanded={isExpanded}
                >
                  {(() => {
                    const iconFailed = section.iconUri ? failedIcons.has(section.id) : false;
                    const showIconImg = section.iconUri && !iconFailed;
                    return (
                      <div
                        className={mergeClasses(styles.sectionIcon, !showIconImg && fallbackIconClass)}
                        style={showIconImg && section.brandColor ? { backgroundColor: section.brandColor } : undefined}
                      >
                        {showIconImg ? (
                          <img
                            src={section.iconUri}
                            alt=""
                            className={styles.sectionIconImg}
                            onError={() => setFailedIcons(prev => new Set(prev).add(section.id))}
                          />
                        ) : (
                          <>
                            {section.type === 'trigger' && <PlayRegular />}
                            {section.type === 'action' && <FlashRegular />}
                            {section.type === 'agentParameters' && <BotRegular />}
                            {section.type === 'variables' && <BracesVariableRegular />}
                            {section.type === 'parameters' && <TagRegular />}
                          </>
                        )}
                      </div>
                    );
                  })()}
                  <div className={styles.sectionInfo}>
                    <Text block className={styles.sectionName}>{section.displayName}</Text>
                  </div>
                  {hasTokens && (
                    <Badge
                      className={styles.sectionBadge}
                      appearance="tint"
                      size="small"
                    >
                      {section.tokens.length}
                    </Badge>
                  )}
                  <ChevronRightRegular
                    className={mergeClasses(
                      styles.sectionChevron,
                      isExpanded && styles.sectionChevronExpanded
                    )}
                  />
                </div>

                {/* Outputs List */}
                {isExpanded && hasTokens && (
                  <div className={styles.outputsList}>
                    {section.tokens.map((token) => renderToken(token))}
                  </div>
                )}

                {/* No outputs message */}
                {isExpanded && !hasTokens && (
                  <div className={styles.outputsList}>
                    <Text size={200} className={styles.noOutputsText}>
                      No outputs available
                    </Text>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <RailHintFooter>Drag outputs to parameter fields</RailHintFooter>
        </>
      )}
    </div>
  );
};

WorkflowInputPanel.displayName = 'WorkflowInputPanel';
